import { useLocalSearchParams, useRouter } from "expo-router";
import { useMemo } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { PinnedCtaFooter } from "../src/components/pinned-cta-footer";
import { ScreenAppbar } from "../src/components/screen-appbar";
import { VerdictPill } from "../src/components/verdict-pill";
import { useDiagnostic } from "../src/diagnostic/use-diagnostic";
import { WeakTopicsList } from "../src/diagnostic/weak-topics-list";
import { ReadinessRing } from "../src/readiness-ring";
import { type Colors, useColors } from "../src/theme";
import { friendlyTrpcError } from "../src/trpc-error";
import { useResponsive } from "../src/use-responsive";
import { useSelectedVertical } from "../src/use-selected-vertical";

// Diagnostic screen — surfaced after free-tier exhaustion. Replaces
// the previous combined EXHAUSTED render branch in practice.tsx. Order
// per spec: eyebrow → ring → pill → body → weak topics. Pinned coral
// "See your options →" advances to /paywall.
export default function Diagnostic(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();
  const vertical = useSelectedVertical();
  const params = useLocalSearchParams<{
    vertical?: string;
    jurisdiction?: string;
  }>();
  const verticalSlug =
    typeof params.vertical === "string"
      ? params.vertical
      : (vertical.verticalSlug ?? null);
  const jurisdictionCode =
    typeof params.jurisdiction === "string"
      ? params.jurisdiction
      : (vertical.jurisdictionCode ?? null);

  const diagnostic = useDiagnostic(verticalSlug);

  function handleSeeOptions(): void {
    const query: Record<string, string> = {};
    if (verticalSlug) query.vertical = verticalSlug;
    if (jurisdictionCode) query.jurisdiction = jurisdictionCode;
    router.push({ pathname: "/paywall", params: query });
  }

  if (diagnostic.error) {
    // The hook surfaces either an already-friendly "No credential
    // selected." or a raw fetch-failure message. Only the latter needs
    // friendly mapping — keep the curated copy intact, and never render
    // a raw tRPC code (#10).
    const isNoCredential = diagnostic.error === "No credential selected.";
    const errorText = isNoCredential
      ? diagnostic.error
      : friendlyTrpcError(diagnostic.error);
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{errorText}</Text>
        {/* A fetch failure is recoverable — offer a retry so this
            high-intent pre-paywall step doesn't dead-end. "No credential
            selected." is a state issue, not retryable, so just go back. */}
        {!isNoCredential ? (
          <Button
            label="Try again"
            onPress={diagnostic.retry}
            accessibilityLabel="Try loading your diagnostic again"
          />
        ) : null}
        <Button label="Back" variant="ghost" onPress={() => router.back()} />
      </View>
    );
  }
  if (!diagnostic.data) {
    return (
      <View style={styles.errorContainer}>
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }

  const v = diagnostic.data;
  const verdictShort =
    v.readiness === "ready"
      ? "Close to ready"
      : v.readiness === "almost"
        ? "Almost ready — a few gaps"
        : "Not ready yet";
  const verdictBody =
    v.readiness === "ready"
      ? `${v.totalCorrect} of ${v.totalAnswered} correct. Push above 90% on the full pool before sitting the exam.`
      : v.readiness === "almost"
        ? `${v.totalCorrect} of ${v.totalAnswered} correct. The full pool keeps targeting your weak areas until they're not weak anymore.`
        : `${v.totalCorrect} of ${v.totalAnswered} correct. Most candidates start here — the adaptive engine zeroes in on what you don't know yet.`;

  const summary = (
    <>
      <Text style={styles.eyebrowCenter}>{`AFTER ${v.totalAnswered} ${v.totalAnswered === 1 ? "QUESTION" : "QUESTIONS"}`}</Text>
      <View style={styles.ringWrap}>
        <ReadinessRing
          pct={v.pct}
          size={isWide ? "xl" : "lg"}
          caption="to ready"
        />
      </View>
      <View style={styles.verdictBlock}>
        <VerdictPill readiness={v.readiness} label={verdictShort} />
        <Text style={styles.verdictBody}>{verdictBody}</Text>
      </View>
    </>
  );
  const cta = (
    <PinnedCtaFooter
      label="See your options →"
      onPress={handleSeeOptions}
      accessibilityLabel="See your options"
    />
  );

  // Rule A (design_handoff_2): ≥900 splits the read summary (left) from
  // the focus list + CTA (right).
  if (isWide) {
    return (
      <View style={styles.outer}>
        <ScreenAppbar title="Your diagnostic" />
        <View style={styles.splitRow}>
          <View style={styles.leftPane}>{summary}</View>
          <View style={styles.rightPane}>
            <ScrollView
              style={styles.rightScroll}
              contentContainerStyle={styles.rightContent}
            >
              <WeakTopicsList topics={v.weakTopics} />
            </ScrollView>
            {cta}
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.outer}>
      <ScreenAppbar title="Your diagnostic" />

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {summary}
        <WeakTopicsList topics={v.weakTopics} />
      </ScrollView>

      {cta}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    outer: { flex: 1, backgroundColor: colors.background },
    errorContainer: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
      gap: space[4],
    },
    scrollContent: {
      padding: space[6],
      paddingBottom: space[10],
      maxWidth: 480,
      alignSelf: "center",
      width: "100%",
    },
    // Rule A split (≥900).
    splitRow: { flex: 1, flexDirection: "row" },
    leftPane: {
      flexBasis: "44%",
      flexGrow: 0,
      backgroundColor: colors.surface,
      borderRightWidth: 1,
      borderRightColor: colors.borderSoft,
      paddingHorizontal: space[8],
      paddingVertical: space[10],
      justifyContent: "center",
    },
    rightPane: { flex: 1 },
    rightScroll: { flex: 1 },
    rightContent: {
      padding: space[8],
      paddingBottom: space[10],
      maxWidth: 560,
      width: "100%",
    },
    eyebrowCenter: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 11,
      letterSpacing: 1.4,
      color: colors.action,
      textAlign: "center",
      marginBottom: space[5],
    },
    ringWrap: { alignItems: "center", marginBottom: space[5] },
    verdictBlock: {
      alignItems: "center",
      gap: space[3],
      marginBottom: space[8],
    },
    verdictBody: {
      ...textStyle.body,
      color: colors.textSecondary,
      textAlign: "center",
      maxWidth: 34 * 8,
    },
    errorText: {
      ...textStyle.body,
      color: colors.error,
      textAlign: "center",
    },
  });
}
